import type { NavigateFunction } from 'react-router-dom';
import type { Notification } from '@/hooks/useNotifications';
import { useChatStore } from '@/store/chatStore';

interface RouteCtx {
  navigate: NavigateFunction;
  isMobile?: boolean;
  markRead?: (id: string) => void;
}

/**
 * Single source of truth for what happens when a notification row is clicked,
 * whether from the chat panel inbox, the desktop dropdown (legacy), or the
 * standalone /notifications page. Marks read, then routes by type.
 */
export function handleNotificationClick(
  n: Notification,
  { navigate, isMobile = false, markRead }: RouteCtx
) {
  if (!n.read && markRead) markRead(n.id);
  const data = n.data ?? {};
  const openChat = useChatStore.getState().open;

  if (n.type === 'inquiry_received') {
    const inquiryId = (data.inquiry_id ?? data.id) as string | undefined;
    openChat(inquiryId ? { inquiryId } : undefined);
    return;
  }
  if (n.type === 'dm_received' && data.conversation_id) {
    const convId = data.conversation_id as string;
    if (isMobile) navigate(`/inbox?thread=${convId}`);
    else openChat({ conversationId: convId });
    return;
  }
  // eBay sale needs-confirmation: land on the pending-sales view of inventory
  // and pop the bulk queue modal on top of it.
  if (n.type === 'ebay_sale') {
    useChatStore.getState().close();
    navigate('/inventory?shortcut=pending_sales');
    // Lazy import to avoid a hook-cycle on module init
    import('@/store/uiStore').then((m) =>
      m.useUIStore.getState().setPendingSalesQueueOpen(true),
    );
    return;
  }
  if (typeof data.action_url === 'string' && data.action_url.startsWith('/')) {
    navigate(data.action_url);
    return;
  }
  if (data.item_id) {
    navigate(`/item/${data.item_id}`);
    return;
  }
  if (['new_offer', 'offer_accepted', 'offer_declined'].includes(n.type)) {
    if (isMobile) navigate('/inbox');
    else openChat();
  }
}
